/**
 * Cota Selection Modal
 * Intermediate step to select a cota before proceeding to sale form
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useGetIdentity } from 'ra-core';
import { fetchCotasByUrl, cancelCotasByUrl } from '@/lib/api-client';
import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';

interface Category {
  id: string;
  categoria_code: string;
  categoria_nome: string | null;
  valor_credito: string | number | null;
  prazo: number | null;
  percentual_mensal: string | number | null;
  parcela_sem_seguro: string | number | null;
  valor_seguro: string | number | null;
  total_parcela: string | number | null;
  observacao: string | null;
  purchase_url: string | null;
}

interface Cota {
  value: string;
  text: string;
}

interface CotaSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: (selectedCota: string | null) => void;
  groupNumber: number;
  category: Category | null;
  adminFee: number | null;
  loading: boolean;
}

export const CotaSelectionModal = ({
  open,
  onClose,
  onContinue,
  groupNumber,
  category,
  loading: externalLoading
}: CotaSelectionModalProps) => {
  const { identity } = useGetIdentity();
  const isMockSale = Boolean(import.meta.env.DEV) && groupNumber === 9999 && category?.categoria_code === 'A1';
  // Conta de demonstração não consulta a Servopa: pula direto para o
  // formulário, como o mock (a finalização é barrada no SaleModal).
  const pulaCotas = isMockSale || identity?.is_demo === true;
  const [cotas, setCotas] = useState<Cota[]>([]);
  const [selectedCota, setSelectedCota] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (pulaCotas) {
      onContinue(null);
      return;
    }
    if (!category || externalLoading) return;
    // Sem identidade ainda não dá pra saber se é demo — buscar agora mandaria
    // a conta de demonstração para a Servopa antes do pulo.
    if (!identity) return;

    // Aborta o fetch ao fechar/reabrir/desmontar — evita empilhar requisições
    // (cada cota fetch segura um slot do Browserless por dezenas de segundos;
    // cancelar+reclicar abriria várias). Obs: o abort para o lado do CLIENTE;
    // liberar o slot no servidor exige tratar req close no /cotas-by-url.
    const controller = new AbortController();

    const run = async () => {
      setLoading(true);
      setError(null);
      setCotas([]);
      setSelectedCota('');

      try {
        if (!category.purchase_url) {
          setError('URL de compra não disponível para esta categoria');
          return;
        }

        const { data: sessao } = await supabase.auth.getSession();
        const data = await fetchCotasByUrl(
          category.purchase_url,
          controller.signal,
          identity?.id,
          sessao.session?.access_token,
        );
        if (controller.signal.aborted) return;

        setCotas(data.cotas || []);
        if (data.cotas && data.cotas.length === 0) {
          setError('Nenhuma cota disponível para esta categoria');
        }
      } catch (err: any) {
        if (controller.signal.aborted || err?.name === 'AbortError') return; // cancelado
        console.error('Error fetching cotas:', err);
        // Mensagem honesta baseada no tipo de erro retornado pelo api-client.
        if (err?.kind === 'upstream_down' || err?.kind === 'network_error') {
          setError(err.message);
        } else if (err?.status && err.status >= 400 && err.status < 500) {
          setError(err.message || 'Não foi possível buscar as cotas disponíveis.');
        } else {
          setError(err?.message || 'Não foi possível verificar cotas disponíveis no momento');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    run();

    return () => controller.abort();
  }, [open, category, externalLoading, pulaCotas, onContinue, identity]);

  // Ao sair da etapa de cotas com uma busca em andamento, manda cancelar no
  // servidor pra soltar o slot do Browserless na hora (o abort do fetch só
  // cancela o lado do cliente). Seguro contra corrida: sair NÃO inicia outra
  // busca de cotas, então não há risco de matar uma busca nova.
  const cancelInFlightSearch = () => {
    if (loading) {
      void supabase.auth.getSession().then(({ data }) => cancelCotasByUrl(identity?.id, data.session?.access_token));
    }
  };

  const handleClose = () => {
    cancelInFlightSearch();
    onClose();
  };

  const handleSkip = () => {
    cancelInFlightSearch();
    onContinue(null);
  };

  const handleContinue = () => {
    if (!selectedCota && cotas.length > 0) {
      setError('Selecione uma cota ou clique em "Pular".');
      return;
    }
    // Send the formatted text (e.g., "0219-5") instead of the value (e.g., "219_5")
    const cotaText = selectedCota ? cotas.find(c => c.value === selectedCota)?.text : null;
    onContinue(cotaText || null);
  };

  if (!category || pulaCotas) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-[calc(100%-1.5rem)] sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Selecionar cota</DialogTitle>
          <DialogDescription>
            {category.categoria_code} - {category.categoria_nome}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {externalLoading || loading ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Carregando cotas disponíveis...</p>
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : cotas.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Nenhuma cota disponível para esta categoria.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="cota">Cota disponível (opcional)</Label>
                <Select value={selectedCota} onValueChange={setSelectedCota}>
                  <SelectTrigger id="cota">
                    <SelectValue placeholder="Selecione uma cota ou pule esta etapa" />
                  </SelectTrigger>
                  <SelectContent>
                    {cotas.map((cota) => (
                      <SelectItem key={cota.value} value={cota.value}>
                        {cota.text}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Sem seleção, a Servopa define a cota na venda.
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          {/* "Pular" fica SEMPRE disponível — inclusive durante o carregamento
              (que pode demorar ou estourar 524) e em caso de erro: segue a venda
              sem pré-selecionar cota (a Servopa escolhe uma no fluxo de /sell). */}
          <Button
            variant="secondary"
            onClick={handleSkip}
            disabled={externalLoading}
          >
            {externalLoading || loading ? 'Pular espera' : 'Pular'}
          </Button>
          {!error && !loading && cotas.length > 0 && (
            <Button
              onClick={handleContinue}
              disabled={externalLoading || !selectedCota}
            >
              Continuar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
